import { FC, ReactElement, useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { formatLocationGroupOptions } from '~/utils/clientAllocation';
import SelectComponent from '~/ui/components/inputs/SelectWithoutAnimation';
import MenuList from '~/ui/components/inputs/SelectWithoutAnimation/components/MenuList';

import MultiValue from '~/ui/pages/ActTeam/reusable/ClientAllocationForm/components/MultiValue/MultiValue';
import Option from '~/ui/pages/ActTeam/reusable/ClientAllocationForm/components/Option/Option';
import ScrollIntoViewMenu from '~/ui/pages/ActTeam/reusable/ClientAllocationForm/components/ScrollIntoViewMenu/ScrollIntoViewMenu';
import {
  LocationGroupWithAllocation,
  NormalizedTeamMembersAllocations,
} from '~/ui/pages/ActTeam/reusable/ClientAllocationForm/shared/model/types';
import { useAddClientAllocationContext } from '~/ui/pages/ActTeam/reusable/ClientAllocationForm/AddClientAllocation/context/AddClientAllocationContext';
import { normalizeLocationGroupOptionToAllocation } from '~/ui/pages/ActTeam/reusable/ClientAllocationForm/EditClientAllocation/utils';
import { IAddAllocationFormValues } from '~/ui/pages/ActTeam/reusable/ClientAllocationForm/AddClientAllocation/types';

import variables from '~/ui/assets/styles/colors.module.scss';

interface IProps {
  teamMemberIdx: number;
  updateLocationGroupAllocations: (groups: LocationGroupWithAllocation[]) => void;
  activeClientIds: number[];
  onDeleteClient: (groupId: number, clientId: number) => void;
  onDeleteGroup: (groupId: number) => void;
}

const AllocationLocationGroupsSelect: FC<IProps> = ({
  teamMemberIdx,
  updateLocationGroupAllocations,
  activeClientIds,
  onDeleteClient,
  onDeleteGroup,
}): ReactElement => {
  const {
    control,
    formState: { errors },
    watch,
  } = useFormContext<IAddAllocationFormValues>();
  const { locationGroups } = useAddClientAllocationContext();

  const selectedFieldName =
    `teamMembersAllocations.${teamMemberIdx}.selectedLocationGroupIds` as const;

  const locationGroupAllocations = watch(
    `teamMembersAllocations.${teamMemberIdx}.locationGroupAllocations` as const,
  );

  const allSelectedLocationGroups: NormalizedTeamMembersAllocations[] = useWatch({
    name: 'teamMembersAllocations',
  });

  const options = useMemo(() => formatLocationGroupOptions(locationGroups), [locationGroups]);

  const existingByGroupId = useMemo(
    () => new Map(locationGroupAllocations.map(lg => [lg.locationGroupId, lg])),
    [locationGroupAllocations],
  );

  const handleSelectGroup = useCallback(
    (groupId: number) => {
      if (existingByGroupId.has(groupId)) {
        onDeleteGroup(groupId);
      } else {
        const groupOption = options.find(o => Number(o.value) === groupId);
        if (groupOption) {
          updateLocationGroupAllocations([
            ...Array.from(existingByGroupId.values()),
            normalizeLocationGroupOptionToAllocation(groupId, groupOption.label),
          ]);
        }
      }
    },
    [existingByGroupId, onDeleteGroup, options, updateLocationGroupAllocations],
  );

  const additionalStyleHandler = useCallback(
    () => ({
      option: (provided: Record<string, unknown>, { data }: { data: { value: number } }) => ({
        ...provided,
        backgroundColor: !new Set(
          allSelectedLocationGroups.flatMap(
            teamMemberAlloc => teamMemberAlloc.selectedLocationGroupIds,
          ),
        ).has(data?.value)
          ? '#FFF1EE'
          : 'inherit',
        color: variables.colorBlack,
      }),
    }),
    [allSelectedLocationGroups],
  );

  return (
    <SelectComponent
      name={selectedFieldName}
      control={control}
      errors={errors}
      closeMenuOnSelect={false}
      menuPortalTarget={null}
      menuPlacement="auto"
      isRelativeWindow={false}
      showCustomComponents
      hideSelectedOptions={false}
      isMulti
      label="Select Location Groups"
      options={options}
      additionalStyleHandler={additionalStyleHandler}
      customComponents={{
        Menu: ScrollIntoViewMenu,
        MenuList: props => <MenuList {...props} label="Location Groups" />,
        MultiValue: props => (
          <MultiValue
            {...props}
            clientIds={activeClientIds}
            locationGroups={locationGroups}
            onDelete={(clientId: number) => onDeleteClient(Number(props.data.value), clientId)}
            onDeleteGroup={onDeleteGroup}
          />
        ),
        Option: props => <Option {...props} selectGroup={handleSelectGroup} />,
      }}
    />
  );
};

export default AllocationLocationGroupsSelect;
